/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { Dispatch, SetStateAction } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleError } from '@/utils/handelErrorMessage';
import { Input } from '@/ux-common/src/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Component } from 'types/api-data';
import { z } from 'zod';

import { useComponents } from '@/hooks/useComponents';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { CustomButton } from '@/components/common';

const WorkflowRepoDialog = ({
  component,
  repoUrl,
  setRepoUrl,
  setRepoLoading,
  openDialog,
  setOpenDialog,
}: {
  component: Component;
  repoUrl: string;
  setRepoUrl: Dispatch<SetStateAction<string>>;
  setRepoLoading: (value: boolean) => void;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
}) => {
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const compIntl = messages?.ComponentsPage;

  const FormSchema = z.object({
    repository_url: z.string().url(),
  });
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      repository_url: repoUrl ?? '',
    },
  });

  const { formState } = form;
  const { isDirty, isSubmitting } = formState;
  const { componentsActions } = useComponents();

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    const formatData = {
      ...component,
      properties: {
        ...component?.properties,
        repository_url: data?.repository_url,
      },
    };
    try {
      setRepoLoading(true);

      await componentsActions.updateComponent(
        component?.component_name,
        formatData
      );

      setRepoUrl(data?.repository_url);

      const toastMsg = repoUrl
        ? compIntl?.repository_url_updated_successfully
        : compIntl?.repository_url_added_successfully;

      toast.success(toastMsg);
    } catch (error) {
      const toastMsg = repoUrl
        ? compIntl?.error_in_updating_repository_url
        : compIntl?.error_in_adding_repository_url;
      handleError(error, toastMsg);
    } finally {
      setRepoLoading(false);
    }
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  return (
    <Dialog open={openDialog} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[640px] bg-gradient-sky-dark"
        aria-describedby={undefined}
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-normal leading-7 text-body-primary">
            {repoUrl ? compIntl?.edit_repository : compIntl?.add_repository}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="repository_url"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel
                    htmlFor="repo_url"
                    className="text-form-input-text font-normal text-form-input-rest"
                  >
                    {compIntl?.repository_url}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="repo_url"
                      className="rounded-sm border border-form-control-border-rest placeholder:text-form-input-text placeholder:text-form-input-disabled"
                      placeholder={compIntl?.enter_repository_url_placeholder}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator className="my-4 bg-line-primary" />
            <DialogFooter className="sm:justify-start">
              <CustomButton
                type="submit"
                customSize="regular"
                customVariant="primary"
                disabled={!isDirty || isSubmitting}
              >
                {repoUrl ? generalIntl?.edit : generalIntl?.add}
              </CustomButton>
              <DialogClose asChild>
                <CustomButton customSize="regular" customVariant="secondary">
                  {generalIntl?.cancel}
                </CustomButton>
              </DialogClose>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowRepoDialog;
